/* eslint-disable sonarjs/no-duplicate-string */
import { PriorityToLogLevelMap } from '../../../../constants';
import { Log } from '../../../../types';
import { config } from './config';

/**
 * Utility for formatted logging with support for dark and light themes.
 */
export class ConsoleLogger {
  private theme: 'light' | 'dark';

  constructor() {
    this.theme = this.initializeTheme();
    this.setupThemeChangeListener();
  }

  /**
   * Initializes the theme based on localStorage or browser settings.
   * @returns 'light' or 'dark'
   */
  private initializeTheme(): 'light' | 'dark' {
    const storedTheme = localStorage.getItem('log-theme');
    if (storedTheme === 'light' || storedTheme === 'dark') {
      return storedTheme;
    }
    return this.detectTheme();
  }

  /**
   * Detects the current console theme based on browser settings.
   * @returns 'light' or 'dark'
   */
  private detectTheme(): 'light' | 'dark' {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }

  /**
   * Sets up a listener to detect changes in the browser's theme settings.
   */
  private setupThemeChangeListener(): void {
    window
      .matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', event => {
        this.theme = event.matches ? 'dark' : 'light';
      });
  }

  /**
   * Manually sets the theme and stores it in localStorage.
   * @param theme - 'light' or 'dark'
   */
  public static setTheme(theme: 'light' | 'dark'): void {
    localStorage.setItem('consoleLoggerTheme', theme);
  }

  /**
   * Formats and filters the stack trace.
   * @param stack - Raw stack trace.
   * @returns Filtered and formatted stack trace.
   */
  private formatStack(stack: string): string[] {
    return stack
      .split('\n')
      .filter(line => !config.stackExclusionPatterns.test(line))
      .map(line => line.replace(/^Error\s*/, '').trim());
  }

  /**
   * Retrieves the appropriate style for a log level based on the theme.
   * @param level - Log level.
   * @returns Console style string.
   */
  private getStyles(level: number): Record<string, string> {
    const themeConfig = config.themes[this.theme];
    return {
      ...themeConfig.baseStyles,
      level: themeConfig.levels[level] || themeConfig.baseStyles.defaultMessage,
      message:
        themeConfig.messages[level] || themeConfig.baseStyles.defaultMessage,
    };
  }

  /**
   * Gets the current time formatted as HH:mm:ss.SSS.
   * @returns Formatted time string.
   */
  private getTime(): string {
    return (
      new Date().toLocaleTimeString('en-US', { hour12: false }) +
      `.${new Date().getMilliseconds().toString().padStart(3, '0')}`
    );
  }

  /**
   * Prints a formatted log entry to the console.
   * @param log - Log object containing level, source, message, and data.
   */
  public print({ level, source, msg, data }: Log, stack?: string): void {
    const [project, module, context] = source.split(':');
    const levelStr = (PriorityToLogLevelMap[level] || 'UNKNOWN').toUpperCase();
    const contextStr = context ? `${context}: ` : '';
    const moduleStr = module ? `:${module}` : '';
    const styles = this.getStyles(level);

    console.groupCollapsed(
      `%c${this.getTime()} %c${levelStr} %c[${project}${moduleStr}] %c${contextStr}%c${msg}`,
      styles.timestamp,
      styles.level,
      styles.source,
      styles.context,
      styles.message,
    );

    if (data && Object.keys(data).length) {
      console.log('Data:', data);
    }

    const formattedStack = this.formatStack(stack || new Error().stack!);

    if (formattedStack.length) {
      console.log('Stack:', formattedStack.join('\n'));
    }

    console.groupEnd();
  }
}
